import { type ApolloError, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { type FundingSourceConfiguration } from '../../../components';

const GET_EVALUATION_FORM_INFO = gql`
  query ($applicationId: String!) {
    researchAssessment(applicationId: $applicationId) {
      type
      publicationName
      _id
    }

    firstDegreeAssessment(applicationId: $applicationId) {
      referenceId
      name
      rankings {
        originalRanking
        rankingSystem
      }
      gpa
      originalGPA
      originalGPAOutOf
      tags
    }

    getFundingSourceConfiguration {
      mapping
      validTags
    }
  }
`;

export type ResearchAssessment = {
  type: string;
  publicationTitle: string;
  _id: string;
};

export type UniversityRanking = {
  originalRanking: string;
  rankingSystem: string;
};

export type FirstDegreeAssessment = {
  gpa: number | string;
  originalGPA: number | string;
  originalGPAOutOf: number | string;
  rankings: UniversityRanking[];
  universityName: string;
  tags?: string[];
};

export type EvaluationInfo = {
  researchAssessment: ResearchAssessment[];
  firstDegreeAssessment: FirstDegreeAssessment;
  fundingSourceConfiguration: FundingSourceConfiguration;
};

type GetEvaluationInfo = {
  evaluationInfo: EvaluationInfo;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
};

export const useGetEvaluationFormInfo = (
  applicationId: string,
): GetEvaluationInfo => {
  const { data, loading, error, refetch } = useQuery(GET_EVALUATION_FORM_INFO, {
    variables: { applicationId },
    skip: !applicationId,
    fetchPolicy: 'network-only',
  });

  const researchAssessment = (data?.researchAssessment ?? []).map(
    (assessment: any) => ({
      type: assessment.type ?? '',
      publicationTitle: assessment?.publicationName ?? '',
      _id: assessment._id ?? '',
    }),
  );

  const firstDegreeAssessment = {
    gpa: data?.firstDegreeAssessment?.gpa ?? 'N/A',
    rankings: (data?.firstDegreeAssessment?.rankings ?? []).map(
      (ranking: UniversityRanking) => ({
        originalRanking: ranking.originalRanking ?? '',
        rankingSystem: ranking.rankingSystem ?? '',
      }),
    ),
    universityName: data?.firstDegreeAssessment?.name ?? '',
    originalGPA: data?.firstDegreeAssessment?.originalGPA ?? 'N/A',
    originalGPAOutOf: data?.firstDegreeAssessment?.originalGPAOutOf ?? 'N/A',
  };

  const getFundingConfig = data?.getFundingSourceConfiguration ?? {};

  const fundingSourceConfiguration: FundingSourceConfiguration = {
    mapping: {
      COMPETITIVE: getFundingConfig?.mapping?.COMPETITIVE ?? [],
      NON_COMPETITIVE: getFundingConfig?.mapping?.NON_COMPETITIVE ?? [],
      OTHER: getFundingConfig?.mapping?.OTHER ?? [],
    },
    validTags: getFundingConfig?.validTags ?? [],
  };

  return {
    evaluationInfo: {
      researchAssessment,
      firstDegreeAssessment,
      fundingSourceConfiguration,
    },
    loading,
    error,
    refetch,
  };
};
